import i18n from '@/i18n'

function luminance(red, green, blue) {
  return [red, green, blue].reduce((sum, value, index) => {
    const channel = value / 255
    return sum + (channel <= 0.04045 ? channel / 12.92 : ((channel + 0.055) / 1.055) ** 2.4) * [0.2126, 0.7152, 0.0722][index]
  }, 0)
}

export function needsLogoBacking(pixels, surface, backing) {
  const light = (hex) => luminance(...[1, 3, 5].map((index) => parseInt(hex.slice(index, index + 2), 16)))
  const contrast = (a, b) => (Math.max(a, b) + 0.05) / (Math.min(a, b) + 0.05)
  const surfaceLight = light(surface), backingLight = light(backing)
  let transparent = 0, visible = 0, surfaceReadable = 0, backedReadable = 0
  for (let i = 0; i < pixels.length; i += 4) {
    if (pixels[i + 3] < 128) { transparent++; continue }
    visible++
    const value = luminance(pixels[i], pixels[i + 1], pixels[i + 2])
    if (contrast(value, surfaceLight) >= 3) surfaceReadable++
    if (contrast(value, backingLight) >= 3) backedReadable++
  }
  return transparent > 0 && visible > 0 && surfaceReadable / visible < 0.75 && backedReadable > surfaceReadable
}

// Preserve the complete mark and its colors. Only transparent pixels receive
// the registered backing; this is deterministic pixel compositing, not editing by AI.
export async function backTransparentLogo(file, policy) {
  if (!policy || !/^#[0-9a-f]{6}$/i.test(policy.surface) || !/^#[0-9a-f]{6}$/i.test(policy.contrastBacking)
      || !['image/png', 'image/webp'].includes(file.type)) return { file }
  const bitmap = await createImageBitmap(file)
  try {
    const sample = document.createElement('canvas')
    sample.width = Math.min(160, bitmap.width); sample.height = Math.min(160, bitmap.height)
    const context = sample.getContext('2d', { willReadFrequently: true })
    context.drawImage(bitmap, 0, 0, sample.width, sample.height)
    if (!needsLogoBacking(context.getImageData(0, 0, sample.width, sample.height).data, policy.surface, policy.contrastBacking)) return { file }
    const canvas = document.createElement('canvas')
    canvas.width = bitmap.width; canvas.height = bitmap.height
    const output = canvas.getContext('2d')
    output.fillStyle = policy.contrastBacking
    output.fillRect(0, 0, canvas.width, canvas.height)
    output.drawImage(bitmap, 0, 0)
    const blob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/png'))
    if (!blob) throw new Error(i18n.t('editorV2.core.errors.logoBackingFailed'))
    return {
      file: new File([blob], file.name.replace(/\.[^.]+$/, '') + '-backed.png', { type: 'image/png' }),
      transformation: `transparent-logo-backing:${policy.contrastBacking}`,
    }
  } finally { bitmap.close() }
}
